if (!customElements.get('wholesale-sticky-bundle-builder')) {
  customElements.define(
    'wholesale-sticky-bundle-builder',
    class WholesaleStickyBundleBuilder extends HTMLElement {
      connectedCallback() {
        this.grid = this.querySelector('[data-wsb-grid]');
        this.products = Array.from(this.querySelectorAll('[data-wsb-product]'));
        this.quickFilters = Array.from(this.querySelectorAll('[data-wsb-quick-filter]'));
        this.allProductsFilter = this.quickFilters.find((button) => button.dataset.filterType === 'all');
        this.advancedOptions = Array.from(this.querySelectorAll('[data-wsb-advanced-option]'));
        this.dialog = this.querySelector('[data-wsb-dialog]');
        this.resultCount = this.querySelector('[data-wsb-result-count]');
        this.emptyState = this.querySelector('[data-wsb-empty]');
        this.filterCount = this.querySelector('[data-wsb-filter-count]');
        this.clearResultButton = this.querySelector('[data-wsb-clear-results]');
        this.summaryList = this.querySelector('[data-wsb-summary-list]');
        this.summaryEmpty = this.querySelector('[data-wsb-summary-empty]');
        this.summaryCount = this.querySelector('[data-wsb-summary-count]');
        this.summarySavings = this.querySelector('[data-wsb-summary-savings]');
        this.summarySubtotal = this.querySelector('[data-wsb-summary-subtotal]');
        this.summaryShippingRow = this.querySelector('[data-wsb-summary-shipping-row]');
        this.minimumWarning = this.querySelector('[data-wsb-minimum-warning]');
        this.reviewCartButton = this.querySelector('[data-wsb-open-cart]');
        this.confirmedItemCount = this.readNumber(this.dataset.cartCount);
        this.currentItemCount = this.confirmedItemCount;
        this.minimumOrderQuantity = Math.max(1, this.readNumber(this.dataset.minimumOrderQuantity) || 20);
        this.freeShippingQuantity = Math.max(1, this.readNumber(this.dataset.freeShippingQuantity) || 50);
        this.discountTiers = [
          {
            threshold: this.readNumber(this.dataset.tier1Threshold),
            discountPercent: this.readNumber(this.dataset.tier1DiscountPercent),
            priceKey: 'wsbTier1Price',
          },
          {
            threshold: this.readNumber(this.dataset.tier2Threshold),
            discountPercent: this.readNumber(this.dataset.tier2DiscountPercent),
            priceKey: 'wsbTier2Price',
          },
          {
            threshold: this.readNumber(this.dataset.tier3Threshold),
            discountPercent: this.readNumber(this.dataset.tier3DiscountPercent),
            priceKey: 'wsbTier3Price',
          },
        ]
          .filter((tier) => tier.threshold > 0 && tier.discountPercent > 0)
          .sort((left, right) => left.threshold - right.threshold);
        this.collectionLoadPromises = new Map();
        this.randomSeed = Math.floor(Math.random() * 4294967295);
        this.cartEventRevision = 0;
        this.cartSyncQueued = false;
        this.selectionOrder = new Map();
        this.nextSelectionOrder = 0;
        this.promotedSelections = new Set();

        this.updateShippingDate();

        if (!this.grid) return;

        this.products.forEach((product, index) => {
          product.dataset.originalIndex = String(index);
        });

        this.setupProductCards();

        if (this.quickFilters.length && !this.quickFilters.some((button) => button.classList.contains('is-active'))) {
          this.quickFilters[0].classList.add('is-active');
          this.quickFilters[0].setAttribute('aria-pressed', 'true');
        }

        const activeQuickFilter = this.quickFilters.find((button) => button.classList.contains('is-active'));
        this.bindEvents();
        this.applyFilterWhenReady(activeQuickFilter, {
          hideGridWhileLoading: activeQuickFilter?.dataset.randomizeProducts === 'true',
        });
        if (this.allProductsFilter !== activeQuickFilter) {
          this.loadCollectionFilter(this.allProductsFilter)?.then(() => {
            const currentFilter = this.quickFilters.find((button) => button.classList.contains('is-active'));
            if (currentFilter?.dataset.filterType === 'available-now') this.applyFilters();
          });
        }
        this.updateProgress(this.confirmedItemCount);
        this.seedInitialSelectionOrder();
        this.renderSelectionSummary();
        this.subscribeToCartUpdates();
        this.bindCartRestoreSync();
        this.syncCartCount();
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
        window.removeEventListener('pageshow', this.handlePageShow);
        window.removeEventListener('focus', this.handleWindowFocus);
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        window.clearTimeout(this.cartSyncTimer);
        document.body.classList.remove('wsb-dialog-open');
      }

      updateShippingDate() {
        const shippingDate = this.querySelector('[data-wsb-shipping-date]');
        if (!shippingDate) return;

        const today = new Date();
        const weekday = today.getDay();
        const daysToAdd = weekday === 5 ? 3 : weekday === 6 ? 2 : 1;
        const dispatchDate = new Date(today);
        dispatchDate.setDate(today.getDate() + daysToAdd);

        try {
          shippingDate.textContent = new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
          }).format(dispatchDate);
        } catch (error) {
          shippingDate.textContent = dispatchDate.toLocaleDateString(undefined, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
          });
        }
      }

      bindEvents() {
        this.quickFilters.forEach((button) => {
          button.addEventListener('click', () => {
            this.grid.style.removeProperty('visibility');
            this.quickFilters.forEach((filterButton) => {
              const isCurrent = filterButton === button;
              filterButton.classList.toggle('is-active', isCurrent);
              filterButton.setAttribute('aria-pressed', String(isCurrent));
            });
            this.applyFilterWhenReady(button);
          });
        });

        this.advancedOptions.forEach((option) => {
          option.addEventListener('click', () => {
            const group = option.dataset.group;
            const shouldActivate = !option.classList.contains('is-active');

            if (group === 'quick_sort') {
              this.advancedOptions
                .filter((candidate) => candidate.dataset.group === group)
                .forEach((candidate) => {
                  candidate.classList.remove('is-active');
                  candidate.setAttribute('aria-pressed', 'false');
                });
            }

            option.classList.toggle('is-active', shouldActivate);
            option.setAttribute('aria-pressed', String(shouldActivate));
            this.applyFilters();
          });
        });

        this.querySelector('[data-wsb-dialog-open]')?.addEventListener('click', () => this.openDialog());
        this.querySelector('[data-wsb-dialog-close]')?.addEventListener('click', () => this.closeDialog());
        this.querySelector('[data-wsb-dialog-apply]')?.addEventListener('click', () => this.closeDialog());
        this.querySelector('[data-wsb-dialog-clear]')?.addEventListener('click', () => this.clearAdvancedFilters());
        this.clearResultButton?.addEventListener('click', () => this.clearAllFilters());
        this.reviewCartButton?.addEventListener('click', () => this.openCart());
        this.summaryList?.addEventListener('click', (event) => this.handleSummaryAction(event));

        this.dialog?.addEventListener('close', () => document.body.classList.remove('wsb-dialog-open'));
        this.dialog?.addEventListener('cancel', () => document.body.classList.remove('wsb-dialog-open'));

        this.addEventListener('input', (event) => this.handleOptimisticQuantity(event));
        this.addEventListener('change', (event) => this.handleOptimisticQuantity(event, { commit: true }));
      }

      openDialog() {
        if (!this.dialog) return;
        document.body.classList.add('wsb-dialog-open');
        if (typeof this.dialog.showModal === 'function') {
          this.dialog.showModal();
        } else {
          this.dialog.setAttribute('open', '');
        }
      }

      closeDialog() {
        if (!this.dialog) return;
        if (typeof this.dialog.close === 'function') {
          this.dialog.close();
        } else {
          this.dialog.removeAttribute('open');
          document.body.classList.remove('wsb-dialog-open');
        }
      }

      applyFilterWhenReady(filterButton, { hideGridWhileLoading = false } = {}) {
        const loadPromise = this.loadCollectionFilter(filterButton);
        if (!loadPromise) {
          this.applyFilters();
          return;
        }

        if (filterButton?.dataset.filterRenderPending === 'true') return;
        if (filterButton) filterButton.dataset.filterRenderPending = 'true';
        if (hideGridWhileLoading) this.grid.style.visibility = 'hidden';
        loadPromise.finally(() => {
          if (filterButton) delete filterButton.dataset.filterRenderPending;
          if (filterButton?.classList.contains('is-active')) this.applyFilters();
          if (hideGridWhileLoading) this.grid.style.removeProperty('visibility');
        });
      }

      clearAdvancedFilters() {
        this.advancedOptions.forEach((option) => {
          const keepFeaturedSort = option.dataset.filterType === 'sort-featured';
          option.classList.toggle('is-active', keepFeaturedSort);
          option.setAttribute('aria-pressed', String(keepFeaturedSort));
        });
        this.applyFilters();
      }

      clearAllFilters() {
        this.quickFilters.forEach((button, index) => {
          const isAll = button.dataset.filterType === 'all' || index === 0;
          button.classList.toggle('is-active', isAll);
          button.setAttribute('aria-pressed', String(isAll));
        });
        this.clearAdvancedFilters();
      }

      setupProductCards() {
        this.products.forEach((product) => {
          const badge = product.querySelector('.card-badge-custom');
          const cardInner = product.querySelector('.card__inner');

          if (badge && cardInner && badge.parentElement !== cardInner) {
            const defaultBadge = cardInner.querySelector('.card__badge');
            if (defaultBadge) defaultBadge.style.display = 'none';
            cardInner.appendChild(badge);
          }

          if (product.dataset.preorder === 'true') this.ensurePreorderMessage(product);
        });

        this.updateProductPrices(this.currentItemCount);
      }

      currentProductPriceElement(product) {
        const price = product.querySelector('.price');
        if (!price) return null;

        const regularPrice = price.querySelector('.price__regular .price-item--regular');
        const salePrice = price.querySelector('.price__sale .price-item--sale');
        return price.classList.contains('price--on-sale') && salePrice ? salePrice : regularPrice || salePrice;
      }

      updateProductPrices(itemCount) {
        const safeItemCount = Math.max(0, this.readNumber(itemCount));
        const activeTier = this.discountTiers.reduce(
          (currentTier, tier) => (safeItemCount >= tier.threshold ? tier : currentTier),
          null
        );
        const priceKey = activeTier?.priceKey || 'wsbBasePrice';

        this.products.forEach((product) => {
          const priceElement = this.currentProductPriceElement(product);
          const nextPrice = product.dataset[priceKey];
          if (!priceElement || !nextPrice) return;

          priceElement.textContent = nextPrice;
          product.classList.toggle('is-volume-discounted', Boolean(activeTier));
          if (activeTier) {
            product.dataset.wsbActiveDiscountPercent = String(activeTier.discountPercent);
          } else {
            delete product.dataset.wsbActiveDiscountPercent;
          }
        });
      }

      ensurePreorderMessage(product) {
        const anchor =
          product.querySelector('quick-add-bulk .quantity-popover-container') ||
          product.querySelector('quick-add-bulk .quantity') ||
          product.querySelector('.quick-order-list .quantity-popover-container') ||
          product.querySelector('.quick-order-list .quantity') ||
          product.querySelector('quick-add-bulk') ||
          product.querySelector('.quick-add-bulk');
        if (!anchor) return;

        const message = `Ships ${this.getPreorderDispatchText(product)}`;
        let note = product.querySelector('.featured-collection-preorder-note');

        if (!note) {
          note = document.createElement('div');
          note.className = 'featured-collection-preorder-note';
        }

        note.textContent = message;
        if (note.nextElementSibling !== anchor) anchor.insertAdjacentElement('beforebegin', note);
      }

      getPreorderDispatchText(product) {
        const dispatchDate = product.dataset.preorderDispatchDate;
        if (dispatchDate) {
          const parsedDate = new Date(`${dispatchDate}T00:00:00`);
          if (!Number.isNaN(parsedDate.getTime())) return this.formatWeekOf(parsedDate);
        }

        const leadTimeDays = this.readNumber(product.dataset.preorderLeadTimeDays);
        if (leadTimeDays > 0) {
          const futureDate = new Date();
          futureDate.setHours(0, 0, 0, 0);
          futureDate.setDate(futureDate.getDate() + leadTimeDays);
          return this.formatWeekOf(futureDate);
        }

        return 'coming soon';
      }

      formatWeekOf(date) {
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        return `week of ${weekStart.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })}`;
      }

      applyFilters() {
        const activeQuickFilter = this.quickFilters.find((button) => button.classList.contains('is-active'));
        const activeAdvancedOptions = this.advancedOptions.filter((option) => option.classList.contains('is-active'));
        const facetGroups = new Map();

        activeAdvancedOptions.forEach((option) => {
          if (option.dataset.group === 'quick_sort') return;
          const group = option.dataset.group || 'custom';
          if (!facetGroups.has(group)) facetGroups.set(group, []);
          facetGroups.get(group).push(option);
        });

        let visibleCount = 0;
        this.products.forEach((product) => {
          const matchesQuick = !activeQuickFilter || this.productMatches(product, activeQuickFilter);
          const matchesAdvanced = Array.from(facetGroups.values()).every((options) =>
            options.some((option) => this.productMatches(product, option))
          );
          const isVisible = matchesQuick && matchesAdvanced;
          product.hidden = !isVisible;
          if (isVisible) visibleCount += 1;
        });

        const sortOption = activeAdvancedOptions.find((option) => option.dataset.group === 'quick_sort');
        this.sortProducts(sortOption?.dataset.filterType || 'sort-featured', activeQuickFilter);
        this.updateFilterUi(visibleCount, activeAdvancedOptions, activeQuickFilter);
      }

      productMatches(product, filter) {
        const filterType = filter.dataset.filterType || 'all';
        const collectionFilterId = filter.dataset.collectionFilterId || '';
        const filterValue = this.normalize(filter.dataset.filterValue);
        const tags = (product.dataset.tags || '')
          .split('||')
          .map((tag) => this.normalize(tag))
          .filter(Boolean);
        const title = this.normalize(product.dataset.title);
        const productType = this.normalize(product.dataset.productType);
        const vendor = this.normalize(product.dataset.vendor);
        const isAvailable = product.dataset.available === 'true';
        const isPreorder = product.dataset.preorder === 'true';

        if (collectionFilterId) {
          return (product.dataset.wsbCollectionFilterIds || '').includes(`|${collectionFilterId}|`);
        }

        if (filter.hasAttribute('data-wsb-quick-filter') && product.dataset.wsbMainSource !== 'true') {
          return false;
        }

        if (filterType === 'available-now' && filter.hasAttribute('data-wsb-quick-filter')) {
          const allProductsCollectionFilterId = this.allProductsFilter?.dataset.collectionFilterId || '';
          if (
            allProductsCollectionFilterId &&
            !(product.dataset.wsbCollectionFilterIds || '').includes(`|${allProductsCollectionFilterId}|`)
          ) {
            return false;
          }
        }

        switch (filterType) {
          case 'all':
          case 'sort-featured':
          case 'sort-newest':
          case 'sort-price-asc':
          case 'sort-price-desc':
          case 'sort-title':
            return true;
          case 'tag':
            return tags.includes(filterValue);
          case 'title':
            return filterValue !== '' && title.includes(filterValue);
          case 'product-type':
            return filterValue !== '' && productType.includes(filterValue);
          case 'vendor':
            return filterValue !== '' && vendor.includes(filterValue);
          case 'available-now':
            return isAvailable && !isPreorder;
          case 'preorder':
            return isPreorder;
          case 'sold-out':
            return !isAvailable;
          case 'best-seller':
            return tags.includes('best seller') || tags.includes('best-seller') || tags.includes('bestseller');
          case 'new-arrival': {
            const days = Math.max(1, this.readNumber(filter.dataset.filterValue) || 30);
            const publishedAt = this.readNumber(product.dataset.createdAt) * 1000;
            return publishedAt > 0 && Date.now() - publishedAt <= days * 86400000;
          }
          default:
            return true;
        }
      }

      sortProducts(filterType, activeQuickFilter) {
        const activeCollectionFilterId = activeQuickFilter?.dataset.collectionFilterId || '';
        const allProductsCollectionFilterId = this.allProductsFilter?.dataset.collectionFilterId || '';
        const prioritizeFeaturedProducts = activeQuickFilter?.dataset.filterType === 'all';
        const collectionOrderFilter = activeCollectionFilterId
          ? activeQuickFilter
          : activeQuickFilter?.dataset.filterType === 'available-now'
            ? this.allProductsFilter
            : null;
        const collectionFilterId =
          activeCollectionFilterId ||
          (activeQuickFilter?.dataset.filterType === 'available-now' ? allProductsCollectionFilterId : '');
        const randomizeProducts = collectionOrderFilter?.dataset.randomizeProducts === 'true';
        const sortedProducts = [...this.products];
        sortedProducts.sort((left, right) => {
          if (prioritizeFeaturedProducts) {
            const leftFeaturedRank = this.featuredRank(left);
            const rightFeaturedRank = this.featuredRank(right);
            const leftIsFeatured = leftFeaturedRank !== Number.MAX_SAFE_INTEGER;
            const rightIsFeatured = rightFeaturedRank !== Number.MAX_SAFE_INTEGER;

            if (leftIsFeatured && !rightIsFeatured) return -1;
            if (!leftIsFeatured && rightIsFeatured) return 1;
            if (leftIsFeatured && rightIsFeatured && leftFeaturedRank !== rightFeaturedRank) {
              return leftFeaturedRank - rightFeaturedRank;
            }
          }

          if (filterType === 'sort-newest') {
            return this.readNumber(right.dataset.createdAt) - this.readNumber(left.dataset.createdAt);
          }
          if (filterType === 'sort-price-asc') {
            return this.readNumber(left.dataset.price) - this.readNumber(right.dataset.price);
          }
          if (filterType === 'sort-price-desc') {
            return this.readNumber(right.dataset.price) - this.readNumber(left.dataset.price);
          }
          if (filterType === 'sort-title') {
            return (left.dataset.title || '').localeCompare(right.dataset.title || '');
          }
          if (collectionFilterId) {
            if (randomizeProducts) {
              return (
                this.randomProductRank(left, collectionFilterId) - this.randomProductRank(right, collectionFilterId)
              );
            }
            return this.collectionRank(left, collectionFilterId) - this.collectionRank(right, collectionFilterId);
          }
          return this.readNumber(left.dataset.originalIndex) - this.readNumber(right.dataset.originalIndex);
        });
        sortedProducts.forEach((product) => this.grid.appendChild(product));
      }

      featuredRank(product) {
        const value = product.dataset.wsbFeaturedRank;
        if (value === undefined || value === '') return Number.MAX_SAFE_INTEGER;
        return this.readNumber(value);
      }

      collectionRank(product, collectionFilterId) {
        const rankToken = (product.dataset.wsbCollectionFilterRanks || '')
          .split('|')
          .find((token) => token.startsWith(`${collectionFilterId}:`));
        if (!rankToken) return Number.MAX_SAFE_INTEGER;
        return this.readNumber(rankToken.split(':')[1]);
      }

      randomProductRank(product, collectionFilterId) {
        const value = `${this.randomSeed}:${collectionFilterId}:${product.dataset.wsbProductId || ''}`;
        let hash = 2166136261;
        for (let index = 0; index < value.length; index += 1) {
          hash ^= value.charCodeAt(index);
          hash = Math.imul(hash, 16777619);
        }
        return hash >>> 0;
      }

      loadCollectionFilter(filterButton) {
        const collectionFilterId = filterButton?.dataset.collectionFilterId || '';
        const collectionUrl = filterButton?.dataset.collectionUrl || '';
        const productLimit = Math.min(300, Math.max(1, this.readNumber(filterButton?.dataset.collectionLimit) || 300));
        const randomizeProducts = filterButton?.dataset.randomizeProducts === 'true';
        if (!collectionFilterId || !collectionUrl || filterButton.dataset.collectionLoaded === 'true') return;

        const loadKey = `${collectionFilterId}:${collectionUrl}:${productLimit}:${randomizeProducts}`;
        if (this.collectionLoadPromises.has(loadKey)) return this.collectionLoadPromises.get(loadKey);

        const loadPromise = this.fetchCollectionProducts(
          collectionUrl,
          collectionFilterId,
          productLimit,
          randomizeProducts
        )
          .then(() => {
            filterButton.dataset.collectionLoaded = 'true';
          })
          .catch(() => {
            // The server-rendered first batch stays usable if an additional page cannot be loaded.
          })
          .finally(() => {
            this.collectionLoadPromises.delete(loadKey);
            this.grid.removeAttribute('aria-busy');
          });

        this.grid.setAttribute('aria-busy', 'true');
        this.collectionLoadPromises.set(loadKey, loadPromise);
        return loadPromise;
      }

      async fetchCollectionProducts(collectionUrl, collectionFilterId, productLimit, randomizeProducts = false) {
        const pageSize = 250;
        const randomCandidateLimit = 300;
        const requestedProductCount = randomizeProducts ? randomCandidateLimit : productLimit;
        const requestedPageCount = Math.ceil(requestedProductCount / pageSize);
        const fetchPage = async (page) => {
          const url = new URL(collectionUrl, window.location.origin);
          url.searchParams.set('view', 'wsb-products');
          url.searchParams.set('page', String(page));
          const response = await fetch(url.toString(), {
            credentials: 'same-origin',
            headers: { Accept: 'text/html' },
          });
          if (!response.ok) throw new Error('Unable to load collection products');
          return new DOMParser().parseFromString(await response.text(), 'text/html');
        };

        const firstDocument = await fetchPage(1);
        const availablePageCount = Math.max(
          1,
          this.readNumber(firstDocument.querySelector('[data-wsb-collection-page]')?.dataset.pageCount)
        );
        const pageCount = Math.min(requestedPageCount, availablePageCount);
        const documents = [firstDocument];
        if (pageCount > 1) {
          documents.push(...(await Promise.all(Array.from({ length: pageCount - 1 }, (_, index) => fetchPage(index + 2)))));
        }

        const loadedCards = documents.flatMap((documentNode) =>
          Array.from(documentNode.querySelectorAll('[data-wsb-collection-page] [data-wsb-product]'))
        );
        const candidateCards = loadedCards.slice(0, requestedProductCount);
        if (randomizeProducts) {
          candidateCards.sort(
            (left, right) =>
              this.randomProductRank(left, collectionFilterId) - this.randomProductRank(right, collectionFilterId)
          );
          this.clearCollectionMembership(collectionFilterId);
        }
        const productById = new Map(this.products.map((product) => [product.dataset.wsbProductId, product]));
        let collectionRank = 0;
        let addedProducts = false;

        for (const loadedCard of candidateCards) {
          if (collectionRank >= productLimit) break;
          const productId = loadedCard.dataset.wsbProductId;
          if (!productId) continue;

          let product = productById.get(productId);
          if (!product) {
            product = loadedCard;
            product.hidden = true;
            product.dataset.originalIndex = String(this.products.length);
            this.grid.appendChild(product);
            this.products.push(product);
            productById.set(productId, product);
            addedProducts = true;
          }

          this.addCollectionMembership(product, collectionFilterId, collectionRank);
          collectionRank += 1;
        }

        if (addedProducts) this.setupProductCards();
        if (this.latestCartData) {
          this.syncQuantityInputs(this.latestCartData.items);
          this.renderSelectionSummary(this.latestCartData.items, this.latestCartData);
        } else {
          this.renderSelectionSummary();
        }
      }

      clearCollectionMembership(collectionFilterId) {
        this.products.forEach((product) => {
          const collectionIds = (product.dataset.wsbCollectionFilterIds || '')
            .split('|')
            .filter((filterId) => filterId && filterId !== collectionFilterId);
          const collectionRanks = (product.dataset.wsbCollectionFilterRanks || '')
            .split('|')
            .filter((rank) => rank && !rank.startsWith(`${collectionFilterId}:`));
          product.dataset.wsbCollectionFilterIds = `|${collectionIds.join('|')}|`;
          product.dataset.wsbCollectionFilterRanks = `|${collectionRanks.join('|')}|`;
        });
      }

      addCollectionMembership(product, collectionFilterId, collectionRank) {
        const idToken = `|${collectionFilterId}|`;
        const currentIds = product.dataset.wsbCollectionFilterIds || '|';
        if (!currentIds.includes(idToken)) {
          product.dataset.wsbCollectionFilterIds = `${currentIds}${collectionFilterId}|`;
        }

        const rankPrefix = `|${collectionFilterId}:`;
        const currentRanks = product.dataset.wsbCollectionFilterRanks || '|';
        if (!currentRanks.includes(rankPrefix)) {
          product.dataset.wsbCollectionFilterRanks = `${currentRanks}${collectionFilterId}:${collectionRank}|`;
        }
      }

      updateFilterUi(visibleCount, activeAdvancedOptions, activeQuickFilter) {
        if (this.resultCount) {
          const template = visibleCount === 1 ? this.dataset.resultSingular : this.dataset.resultPlural;
          this.resultCount.textContent = this.replaceToken(template, 'count', visibleCount);
        }

        if (this.emptyState) this.emptyState.hidden = visibleCount !== 0;

        const activeFacetCount = activeAdvancedOptions.filter(
          (option) => option.dataset.group !== 'quick_sort' || option.dataset.filterType !== 'sort-featured'
        ).length;
        if (this.filterCount) {
          this.filterCount.textContent = String(activeFacetCount);
          this.filterCount.hidden = activeFacetCount === 0;
        }

        const quickFilterIsActive = activeQuickFilter && activeQuickFilter.dataset.filterType !== 'all';
        this.classList.toggle('has-active-filters', activeFacetCount > 0 || quickFilterIsActive);
        if (this.clearResultButton) {
          this.clearResultButton.hidden = activeFacetCount === 0 && !quickFilterIsActive;
        }
      }

      subscribeToCartUpdates() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          const cartData = event?.cartData;
          this.cartEventRevision += 1;
          if (event?.source === 'quick-add') {
            this.scheduleCartSync();
            return;
          }
          if (Array.isArray(cartData?.items) && Number.isFinite(Number(cartData?.item_count))) {
            this.applyCartState(cartData);
          } else {
            this.scheduleCartSync();
          }
        });
      }

      bindCartRestoreSync() {
        this.handlePageShow = (event) => {
          if (event.persisted) this.scheduleCartSync();
        };
        this.handleWindowFocus = () => this.scheduleCartSync();
        this.handleVisibilityChange = () => {
          if (document.visibilityState === 'visible') this.scheduleCartSync();
        };

        window.addEventListener('pageshow', this.handlePageShow);
        window.addEventListener('focus', this.handleWindowFocus);
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
      }

      scheduleCartSync(delay = 80) {
        window.clearTimeout(this.cartSyncTimer);
        this.cartSyncTimer = window.setTimeout(() => {
          if (!this.isConnected) return;
          if (this.hasPendingBulkUpdates()) {
            this.scheduleCartSync();
            return;
          }
          this.syncCartCount();
        }, delay);
      }

      hasPendingBulkUpdates() {
        return Array.from(this.querySelectorAll('quick-add-bulk')).some(
          (bulkAdd) => bulkAdd.requestStarted || bulkAdd.queue?.length
        );
      }

      async syncCartCount() {
        if (this.cartSyncPromise) {
          this.cartSyncQueued = true;
          return this.cartSyncPromise;
        }

        const eventRevisionAtStart = this.cartEventRevision;
        this.cartSyncPromise = this.fetchCartState(eventRevisionAtStart);

        try {
          await this.cartSyncPromise;
        } finally {
          this.cartSyncPromise = null;
          if (this.cartSyncQueued && this.isConnected) {
            this.cartSyncQueued = false;
            this.syncCartCount();
          }
        }
      }

      async fetchCartState(eventRevisionAtStart) {
        try {
          const cartPath = window.routes?.cart_url || '/cart';
          const response = await fetch(`${cartPath}.js`, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            cache: 'no-store',
          });
          if (!response.ok) return;

          const cart = await response.json();
          if (eventRevisionAtStart !== this.cartEventRevision) {
            this.cartSyncQueued = true;
            return;
          }
          this.applyCartState(cart);
        } catch (error) {
          // The server-rendered cart count remains a safe fallback when the request is unavailable.
        }
      }

      applyCartState(cart) {
        if (!cart || !Array.isArray(cart.items)) return;

        const itemCount = Number.isFinite(Number(cart.item_count))
          ? this.readNumber(cart.item_count)
          : cart.items.reduce((total, item) => total + this.readNumber(item.quantity), 0);
        this.latestCartData = cart;
        this.confirmedItemCount = itemCount;
        this.dataset.cartCount = String(itemCount);
        this.promotedSelections.clear();
        this.syncQuantityInputs(cart.items);
        this.renderHeaderCartCount(itemCount);
        this.updateProgress(itemCount);
        this.renderSelectionSummary(cart.items, cart);
      }

      syncQuantityInputs(cartItems) {
        const quantityByVariant = new Map();
        (Array.isArray(cartItems) ? cartItems : []).forEach((item) => {
          const variantId = String(item.variant_id || item.id || '');
          if (!variantId) return;
          quantityByVariant.set(
            variantId,
            (quantityByVariant.get(variantId) || 0) + this.readNumber(item.quantity)
          );
        });

        this.products.forEach((product) => {
          const input = product.querySelector('input.quantity__input');
          const variantId = String(input?.dataset.quantityVariantId || '');
          if (!input || !variantId) return;

          const quantity = quantityByVariant.get(variantId) || 0;
          const bulkAdd = input.closest('quick-add-bulk');
          const inputValue = this.readNumber(input.value);
          const pendingQuantity = input.dataset.wsbPendingQuantity;
          const pendingValue = pendingQuantity === undefined ? null : this.readNumber(pendingQuantity);
          const nativePending =
            bulkAdd?.optimisticQuantities?.has(variantId) ||
            bulkAdd?.queue?.some((item) => String(item.id) === variantId) ||
            bulkAdd?.requestStarted;
          const isEditing = document.activeElement === input || input.dataset.wsbEditing === 'true';

          if (isEditing) return;
          if (pendingValue !== null && inputValue === quantity) {
            delete input.dataset.wsbPendingQuantity;
            delete input.dataset.wsbPendingUntil;
          } else if (
            pendingValue !== null &&
            inputValue === pendingValue &&
            (nativePending || Date.now() < this.readNumber(input.dataset.wsbPendingUntil))
          ) {
            return;
          }

          input.value = String(quantity);
          input.setAttribute('value', String(quantity));
          input.dataset.cartQuantity = String(quantity);
          delete input.dataset.wsbPendingQuantity;
          delete input.dataset.wsbPendingUntil;
          bulkAdd?.optimisticQuantities?.delete(variantId);
        });
      }

      renderHeaderCartCount(itemCount) {
        const cartLink = document.getElementById('cart-icon-bubble');
        if (!cartLink) return;

        const safeCount = Math.max(0, this.readNumber(itemCount));
        let bubble = cartLink.querySelector('.cart-count-bubble');
        if (safeCount === 0) {
          bubble?.remove();
          return;
        }

        if (!bubble) {
          bubble = document.createElement('div');
          bubble.className = 'cart-count-bubble';
          bubble.innerHTML = '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
          cartLink.appendChild(bubble);
        }

        bubble.dataset.cartCount = String(safeCount);
        let visibleCount = bubble.querySelector('[aria-hidden="true"]');
        if (safeCount < 100) {
          if (!visibleCount) {
            visibleCount = document.createElement('span');
            visibleCount.setAttribute('aria-hidden', 'true');
            bubble.prepend(visibleCount);
          }
          visibleCount.textContent = String(safeCount);
        } else {
          visibleCount?.remove();
        }

        let accessibleCount = bubble.querySelector('.visually-hidden');
        if (!accessibleCount) {
          accessibleCount = document.createElement('span');
          accessibleCount.className = 'visually-hidden';
          bubble.appendChild(accessibleCount);
        }
        const currentLabel = accessibleCount.textContent.trim();
        const localizedNumber = /\d(?:[\d,.\u00a0\u202f ]*\d)?/;
        accessibleCount.textContent = localizedNumber.test(currentLabel)
          ? currentLabel.replace(localizedNumber, String(safeCount))
          : String(safeCount);
      }

      handleOptimisticQuantity(event, { commit = false } = {}) {
        const input = event.target.closest?.('input.quantity__input');
        if (!input || !this.contains(input)) return;

        const nextQuantity = this.readNumber(input.value);
        const confirmedQuantity = this.readNumber(input.dataset.cartQuantity);
        if (commit) {
          delete input.dataset.wsbEditing;
          input.dataset.wsbPendingQuantity = String(nextQuantity);
          input.dataset.wsbPendingUntil = String(Date.now() + 5000);
        } else {
          input.dataset.wsbEditing = 'true';
        }
        const selectionKey = this.selectionKeyForVariant(input.dataset.quantityVariantId);
        if (nextQuantity > 0 && confirmedQuantity === 0 && !this.promotedSelections.has(selectionKey)) {
          this.promoteSelection(selectionKey);
          this.promotedSelections.add(selectionKey);
        }

        const optimisticCount = Array.from(this.querySelectorAll('input.quantity__input')).reduce((total, quantityInput) => {
          const confirmedQuantity = this.readNumber(quantityInput.dataset.cartQuantity);
          const nextQuantity = this.readNumber(quantityInput.value);
          return total + nextQuantity - confirmedQuantity;
        }, this.confirmedItemCount);

        this.updateProgress(Math.max(0, optimisticCount));
        this.renderSelectionSummary();
        if (commit) this.scheduleCartSync(700);
      }

      handleSummaryAction(event) {
        const button = event.target.closest?.('[data-wsb-summary-action]');
        if (!button || !this.summaryList?.contains(button)) return;

        const item = button.closest('[data-selection-key]');
        const variantId = String(button.dataset.variantId || item?.dataset.variantId || '');
        const currentQuantity = this.readNumber(item?.dataset.quantity);
        const input = this.products
          .map((product) => product.querySelector('input.quantity__input'))
          .find((candidate) => String(candidate?.dataset.quantityVariantId || '') === variantId);
        const step = Math.max(1, this.readNumber(input?.step) || 1);
        let nextQuantity = currentQuantity;

        if (button.dataset.wsbSummaryAction === 'increase') nextQuantity += step;
        if (button.dataset.wsbSummaryAction === 'decrease') nextQuantity = Math.max(0, currentQuantity - step);
        if (button.dataset.wsbSummaryAction === 'remove') nextQuantity = 0;
        if (input?.max) nextQuantity = Math.min(nextQuantity, this.readNumber(input.max));
        if (nextQuantity === currentQuantity) return;

        if (input) {
          input.value = String(nextQuantity);
          input.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }

        this.updateSummaryVariant(variantId, nextQuantity);
      }

      async updateSummaryVariant(variantId, quantity) {
        if (!variantId || this.summaryUpdatePromise) return;

        this.summaryList?.setAttribute('aria-busy', 'true');
        const cartUpdatePath = window.routes?.cart_update_url || '/cart/update';
        this.summaryUpdatePromise = fetch(cartUpdatePath, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ updates: { [variantId]: quantity } }),
        });

        try {
          const response = await this.summaryUpdatePromise;
          if (!response.ok) return;
          const cart = await response.json();
          this.applyCartState(cart);
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'wsb-summary', cartData: cart });
          }
        } catch (error) {
          this.scheduleCartSync();
        } finally {
          this.summaryUpdatePromise = null;
          this.summaryList?.removeAttribute('aria-busy');
        }
      }

      updateProgress(itemCount) {
        this.currentItemCount = Math.max(0, this.readNumber(itemCount));
        this.updateProductPrices(this.currentItemCount);
        if (this.currentItemCount >= this.minimumOrderQuantity) this.hideMinimumWarning();
        if (this.summaryShippingRow) this.summaryShippingRow.hidden = this.currentItemCount < this.freeShippingQuantity;

        this.querySelectorAll('[data-wsb-progress]').forEach((progress) => {
          const tiers = Array.from(progress.querySelectorAll('[data-wsb-tier]'))
            .map((element) => ({
              element,
              threshold: this.readNumber(element.dataset.threshold),
              message: element.dataset.message || '',
            }))
            .filter((tier) => tier.threshold > 0)
            .sort((left, right) => left.threshold - right.threshold);
          if (!tiers.length) return;

          const lastThreshold = tiers[tiers.length - 1].threshold;
          const nextTier = tiers.find((tier) => itemCount < tier.threshold);
          const message = progress.querySelector('[data-wsb-progress-message]');
          const count = progress.querySelector('[data-wsb-progress-count]');
          const track = progress.querySelector('[role="progressbar"]');

          if (message) {
            message.textContent = nextTier
              ? this.replaceToken(nextTier.message, 'count', nextTier.threshold - itemCount)
              : progress.dataset.completeMessage;
          }
          if (count) count.textContent = this.replaceToken(progress.dataset.countLabel, 'count', itemCount);
          if (track) {
            track.setAttribute('aria-valuemax', String(lastThreshold));
            track.setAttribute('aria-valuenow', String(Math.min(itemCount, lastThreshold)));
          }

          let previousThreshold = 0;
          tiers.forEach((tier) => {
            const isUnlocked = itemCount >= tier.threshold;
            const segment = tier.element.closest('[data-wsb-progress-segment]');
            const fill = segment?.querySelector('[data-wsb-progress-segment-fill]');
            const segmentRange = Math.max(1, tier.threshold - previousThreshold);
            const segmentProgress = Math.min(
              100,
              Math.max(0, ((itemCount - previousThreshold) / segmentRange) * 100)
            );
            if (fill) fill.style.width = `${segmentProgress}%`;
            tier.element.classList.toggle('is-unlocked', isUnlocked);
            tier.element.setAttribute('aria-current', isUnlocked ? 'step' : 'false');
            previousThreshold = tier.threshold;
          });
        });
      }

      renderSelectionSummary(cartItems, cartData) {
        if (!this.summaryList) return;

        const selections = Array.isArray(cartItems)
          ? this.selectionFromCartItems(cartItems)
          : this.selectionFromQuantityInputs();
        const totalQuantity = selections.reduce((total, selection) => total + selection.quantity, 0);
        const fragment = document.createDocumentFragment();
        const existingItems = new Map(
          Array.from(this.summaryList.children).map((item) => [item.dataset.selectionKey, item])
        );

        selections.forEach((selection) => {
          let item = existingItems.get(selection.key);
          if (!item) {
            item = document.createElement('li');
            item.className = 'wsb-summary__item';
            item.dataset.selectionKey = selection.key;

            const image = document.createElement('img');
            image.className = 'wsb-summary__image';
            image.alt = '';
            image.width = 54;
            image.height = 54;
            image.loading = 'lazy';
            image.decoding = 'async';

            const title = document.createElement('p');
            title.className = 'wsb-summary__product-title';

            const controls = document.createElement('div');
            controls.className = 'wsb-summary__controls';

            const stepper = document.createElement('div');
            stepper.className = 'wsb-summary__stepper';

            const decrease = document.createElement('button');
            decrease.type = 'button';
            decrease.className = 'wsb-summary__stepper-button';
            decrease.dataset.wsbSummaryAction = 'decrease';
            decrease.textContent = '−';

            const quantity = document.createElement('span');
            quantity.className = 'wsb-summary__quantity';

            const increase = document.createElement('button');
            increase.type = 'button';
            increase.className = 'wsb-summary__stepper-button';
            increase.dataset.wsbSummaryAction = 'increase';
            increase.textContent = '+';

            const remove = document.createElement('button');
            remove.type = 'button';
            remove.className = 'wsb-summary__remove';
            remove.dataset.wsbSummaryAction = 'remove';
            remove.textContent = '×';

            stepper.append(decrease, quantity, increase);
            controls.append(stepper, remove);

            const details = document.createElement('div');
            details.className = 'wsb-summary__details';
            details.append(title, controls);
            item.append(image, details);
          }

          const image = item.querySelector('.wsb-summary__image');
          const title = item.querySelector('.wsb-summary__product-title');
          const quantity = item.querySelector('.wsb-summary__quantity');
          const decrease = item.querySelector('[data-wsb-summary-action="decrease"]');
          const increase = item.querySelector('[data-wsb-summary-action="increase"]');
          const remove = item.querySelector('[data-wsb-summary-action="remove"]');
          item.dataset.variantId = selection.variantId;
          item.dataset.quantity = String(selection.quantity);
          if (image) {
            image.hidden = !selection.image;
            if (selection.image && image.getAttribute('src') !== selection.image) image.src = selection.image;
          }
          if (title) title.textContent = selection.title;
          if (quantity) {
            quantity.textContent = String(selection.quantity);
            quantity.setAttribute('aria-label', `${selection.quantity} selected`);
          }
          [decrease, increase, remove].forEach((control) => {
            if (control) control.dataset.variantId = selection.variantId;
          });
          if (decrease) decrease.setAttribute('aria-label', `Decrease quantity for ${selection.title}`);
          if (increase) increase.setAttribute('aria-label', `Increase quantity for ${selection.title}`);
          if (remove) remove.setAttribute('aria-label', `Remove ${selection.title}`);

          fragment.appendChild(item);
        });

        this.summaryList.replaceChildren(fragment);
        if (this.summaryEmpty) this.summaryEmpty.hidden = selections.length > 0;
        if (this.summaryCount) this.summaryCount.textContent = String(totalQuantity);
        this.updateSummaryTotals(selections, cartData);
      }

      selectionFromQuantityInputs() {
        const selections = new Map();

        this.products.forEach((product) => {
          const input = product.querySelector('input.quantity__input');
          const quantity = this.readNumber(input?.value);
          if (quantity <= 0) return;
          const unitPrice = this.readNumber(product.dataset.summaryPriceCents);
          this.addSummarySelection(selections, product, quantity, {
            totalPrice: unitPrice * quantity,
            originalTotalPrice: unitPrice * quantity,
          });
        });

        return this.sortSummarySelections(selections);
      }

      selectionFromCartItems(cartItems) {
        const productByVariant = new Map();
        this.products.forEach((product) => {
          const variantId = product.querySelector('input.quantity__input')?.dataset.quantityVariantId;
          if (variantId) productByVariant.set(String(variantId), product);
        });

        [...cartItems].reverse().forEach((cartItem) => {
          this.selectionOrderFor(this.selectionKeyForVariant(cartItem.variant_id || cartItem.id));
        });

        const selections = new Map();
        cartItems.forEach((cartItem) => {
          const variantId = String(cartItem.variant_id || cartItem.id || '');
          const product = productByVariant.get(variantId);
          const quantity = this.readNumber(cartItem.quantity);
          if (quantity <= 0) return;
          if (!product) {
            const selectionId = this.selectionKeyForVariant(variantId);
            const totalPrice = this.readMoneyValue(
              cartItem.final_line_price,
              this.readMoneyValue(cartItem.final_price, cartItem.price) * quantity
            );
            const originalTotalPrice = Math.max(
              totalPrice,
              this.readMoneyValue(
                cartItem.original_line_price,
                this.readMoneyValue(cartItem.original_price, cartItem.price) * quantity
              )
            );
            const existing = selections.get(selectionId);
            if (existing) {
              existing.quantity += quantity;
              existing.totalPrice += totalPrice;
              existing.originalTotalPrice += originalTotalPrice;
            } else {
              selections.set(selectionId, {
                key: selectionId,
                order: this.selectionOrderFor(selectionId),
                variantId,
                title: cartItem.product_title || cartItem.title || '',
                image: cartItem.image || cartItem.featured_image?.url || '',
                quantity,
                totalPrice,
                originalTotalPrice,
              });
            }
            return;
          }
          const unitPrice = this.readNumber(product.dataset.summaryPriceCents);
          const totalPrice = this.readMoneyValue(cartItem.final_line_price, unitPrice * quantity);
          const originalTotalPrice = this.readMoneyValue(
            cartItem.original_line_price,
            unitPrice * quantity
          );
          this.addSummarySelection(selections, product, quantity, {
            variantId,
            totalPrice,
            originalTotalPrice: Math.max(totalPrice, originalTotalPrice),
          });
        });

        return this.sortSummarySelections(selections);
      }

      addSummarySelection(selections, product, quantity, pricing = {}) {
        const input = product.querySelector('input.quantity__input');
        const variantId = String(pricing.variantId || input?.dataset.quantityVariantId || '');
        const selectionKey = this.selectionKeyForVariant(variantId || product.dataset.wsbProductId);
        const existing = selections.get(selectionKey);
        if (existing) {
          existing.quantity += quantity;
          existing.totalPrice += this.readNumber(pricing.totalPrice);
          existing.originalTotalPrice += this.readNumber(pricing.originalTotalPrice);
          return;
        }

        const fallbackImage = product.querySelector('.card__media img');
        selections.set(selectionKey, {
          key: selectionKey,
          order: this.selectionOrderFor(selectionKey),
          variantId,
          title: product.dataset.summaryTitle || product.dataset.title || '',
          image: product.dataset.summaryImage || fallbackImage?.currentSrc || fallbackImage?.src || '',
          quantity,
          totalPrice: this.readNumber(pricing.totalPrice),
          originalTotalPrice: this.readNumber(pricing.originalTotalPrice),
        });
      }

      selectionOrderFor(selectionKey) {
        if (!this.selectionOrder.has(selectionKey)) {
          this.selectionOrder.set(selectionKey, this.nextSelectionOrder);
          this.nextSelectionOrder += 1;
        }
        return this.selectionOrder.get(selectionKey);
      }

      selectionKeyForVariant(variantId) {
        return `variant-${String(variantId || '')}`;
      }

      promoteSelection(selectionKey) {
        this.selectionOrder.set(selectionKey, this.nextSelectionOrder);
        this.nextSelectionOrder += 1;
      }

      seedInitialSelectionOrder() {
        [...this.products].reverse().forEach((product) => {
          const input = product.querySelector('input.quantity__input');
          if (this.readNumber(input?.value) <= 0) return;
          this.selectionOrderFor(this.selectionKeyForVariant(input.dataset.quantityVariantId));
        });
      }

      sortSummarySelections(selections) {
        return Array.from(selections.values()).sort((left, right) => right.order - left.order);
      }

      openCart() {
        if (this.currentItemCount < this.minimumOrderQuantity) {
          this.showMinimumWarning();
          return;
        }

        this.hideMinimumWarning();
        const cartTrigger = document.getElementById('cart-icon-bubble');
        if (cartTrigger) {
          cartTrigger.click();
          return;
        }
        window.location.assign(window.routes?.cart_url || '/cart');
      }

      updateSummaryTotals(selections, cartData) {
        const hasCartTotal = Number.isFinite(Number(cartData?.total_price));
        if (!hasCartTotal) return;

        const currency = cartData?.currency || this.dataset.cartCurrency;
        const totalQuantity = selections.reduce((total, selection) => total + selection.quantity, 0);
        const discountPercent = this.activeDiscountPercent(totalQuantity);
        const subtotal = this.readMoneyValue(cartData.total_price, 0);
        const originalSubtotal = this.readMoneyValue(
          cartData.original_total_price,
          subtotal + this.readMoneyValue(cartData.total_discount, 0)
        );

        const existingSavings = Math.max(0, originalSubtotal - subtotal);
        const volumeDiscountAlreadyApplied = this.hasMatchingCartPercentageDiscount(cartData, discountPercent);
        const volumeSavings =
          discountPercent > 0 && !volumeDiscountAlreadyApplied
            ? Math.round((subtotal * discountPercent) / 100)
            : 0;
        const discountedSubtotal = Math.max(0, subtotal - volumeSavings);
        const savings = existingSavings + volumeSavings;
        if (this.summarySavings) {
          const formattedSavings = this.formatMoney(savings, currency);
          this.summarySavings.textContent = savings > 0 ? `-${formattedSavings}` : formattedSavings;
        }
        if (this.summarySubtotal) this.summarySubtotal.textContent = this.formatMoney(discountedSubtotal, currency);
      }

      activeDiscountPercent(itemCount) {
        return this.discountTiers.reduce(
          (activeDiscount, tier) =>
            itemCount >= tier.threshold ? tier.discountPercent : activeDiscount,
          0
        );
      }

      hasMatchingCartPercentageDiscount(cartData, discountPercent) {
        if (!cartData || discountPercent <= 0) return false;

        const applications = [
          ...(cartData.cart_level_discount_applications || []),
          ...(cartData.discount_applications || []),
        ];
        (cartData.items || []).forEach((item) => {
          [...(item.discount_allocations || []), ...(item.line_level_discount_allocations || [])].forEach(
            (allocation) => {
              if (allocation?.discount_application) applications.push(allocation.discount_application);
            }
          );
        });

        return applications.some(
          (application) =>
            this.normalize(application?.value_type) === 'percentage' &&
            Math.abs(Number(application?.value) - discountPercent) < 0.01
        );
      }

      showMinimumWarning() {
        if (!this.minimumWarning) return;
        this.minimumWarning.textContent =
          this.dataset.minimumOrderWarning || 'A 20 pair minimum order quantity is required';
        this.minimumWarning.hidden = false;
        this.minimumWarning.focus({ preventScroll: true });
      }

      hideMinimumWarning() {
        if (this.minimumWarning) this.minimumWarning.hidden = true;
      }

      formatMoney(cents, currency) {
        const currencyCode = currency || this.dataset.cartCurrency || 'USD';
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'en', {
            style: 'currency',
            currency: currencyCode,
          }).format(this.readNumber(cents) / 100);
        } catch (error) {
          return `${(this.readNumber(cents) / 100).toFixed(2)} ${currencyCode}`;
        }
      }

      readMoneyValue(value, fallback) {
        const parsedValue = Number(value);
        return Number.isFinite(parsedValue) ? Math.round(parsedValue) : this.readNumber(fallback);
      }

      replaceToken(template, token, value) {
        return String(template || '').replaceAll(`[${token}]`, String(value));
      }

      normalize(value) {
        return String(value || '').trim().toLowerCase();
      }

      readNumber(value) {
        const parsedValue = Number.parseInt(value, 10);
        return Number.isFinite(parsedValue) ? parsedValue : 0;
      }
    }
  );
}
